import { useState } from 'react'

import {
  Box,
  Radio,
  Text,
  Title,
  WizardStep,
  WizardStepRenderParams,
} from '@factorialco/deprecated-design-system'

import Callout from 'modules/core/components/Callout'
import MoneyField from 'modules/core/components/MoneyField'
import { fromCents, toCents } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { HealthcarePlanData } from '../types'
import { dependentsStepSchema } from '../validation'

const DependentsStep = ({
  data,
  onDataChanged,
  locale,
  currency,
}: WizardStepRenderParams<HealthcarePlanData> & { locale: string; currency: string }) => {
  // Track which fields have been touched/blurred to show validation errors
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set())

  const selectedValue = data.allowDependents ? 'yes' : data.allowDependents === false ? 'no' : ''

  // Helper to mark field as touched
  const markFieldTouched = (fieldName: string) => {
    setTouchedFields((prev) => new Set(prev).add(fieldName))
  }

  // Use Zod validation from validation.ts
  const validationResult = dependentsStepSchema(locale, currency).safeParse(data)

  // Extract field-specific errors from Zod validation, but only show if field was touched
  const getFieldError = (fieldName: string): string | undefined => {
    if (validationResult.success || !touchedFields.has(fieldName)) return undefined

    const fieldError = validationResult.error.errors.find((error) => error.path.includes(fieldName))
    return fieldError?.message
  }

  const errors = {
    dependentCostCents: getFieldError('dependentCostCents'),
    coveredDependentCostCents: getFieldError('coveredDependentCostCents'),
  }

  const handleDependentsToggle = (value: string) => {
    const supports = value === 'yes'

    if (supports) {
      onDataChanged({
        ...data,
        allowDependents: true,
      })
    } else {
      onDataChanged({
        ...data,
        allowDependents: false,
      })
    }
  }

  const totalCostEuros =
    data.dependentCostCents === undefined ? undefined : fromCents(data.dependentCostCents)
  const companyCostEuros =
    data.coveredDependentCostCents === undefined
      ? undefined
      : fromCents(data.coveredDependentCostCents)
  const dependentEmployeeCostEuros =
    totalCostEuros === undefined || companyCostEuros === undefined
      ? undefined
      : Math.max(0, totalCostEuros - companyCostEuros)

  return (
    <Box flexDirection="column" gap="s24" width="s360">
      <Box flexDirection="column" gap="s8">
        <Title
          title={i18n.t('benefits.healthcare.plan_wizard.dependents_step.title')}
          description={i18n.t('benefits.healthcare.plan_wizard.dependents_step.description')}
        />
      </Box>

      <Box flexDirection="column" gap="s24">
        <Box flexDirection="row" gap="s16">
          <Box flexGrow>
            <Radio
              name="dependents"
              option={{
                disabled: false,
                label: i18n.t('benefits.healthcare.plan_wizard.dependents_step.yes'),
                value: 'yes',
              }}
              value={selectedValue}
              onChange={handleDependentsToggle}
            />
          </Box>
          <Box flexGrow>
            <Radio
              name="dependents"
              option={{
                disabled: false,
                label: i18n.t('benefits.healthcare.plan_wizard.dependents_step.no'),
                value: 'no',
              }}
              value={selectedValue}
              onChange={handleDependentsToggle}
            />
          </Box>
        </Box>

        {selectedValue === 'yes' && (
          <>
            <Box flexDirection="column" gap="s24">
              <MoneyField
                label={i18n.t(
                  'benefits.healthcare.plan_wizard.employee_cost_step.total_cost.label'
                )}
                mandatory
                currency={currency}
                value={totalCostEuros !== undefined ? String(totalCostEuros) : ''}
                onChange={(value: string) => {
                  onDataChanged({
                    ...data,
                    dependentCostCents: value.length > 0 ? toCents(Number(value)) : undefined,
                  })
                }}
                onBlur={() => markFieldTouched('dependentCostCents')}
                placeholder="0"
                error={errors.dependentCostCents}
              />
              <Box gap="s8">
                <Text size="200" weight="semibold" color="grey800">
                  {i18n.t('common.paid_by')}
                </Text>
                <Box
                  flexDirection="row"
                  gap="s16"
                  padding="s16"
                  border={{ all: { width: 's1', style: 'solid', color: 'grey400' } }}
                  borderRadius={{ all: 'abs016' }}
                >
                  <Box flexGrow>
                    <MoneyField
                      label={i18n.t('common.company')}
                      mandatory
                      currency={currency}
                      value={companyCostEuros !== undefined ? String(companyCostEuros) : ''}
                      onChange={(value: string) => {
                        onDataChanged({
                          ...data,
                          coveredDependentCostCents:
                            value.length > 0 ? toCents(Number(value)) : undefined,
                        })
                      }}
                      onBlur={() => markFieldTouched('coveredDependentCostCents')}
                      placeholder="0"
                      error={errors.coveredDependentCostCents}
                    />
                  </Box>

                  <Box flexGrow>
                    <MoneyField
                      label={i18n.t('common.employee')}
                      currency={currency}
                      value={
                        dependentEmployeeCostEuros !== undefined
                          ? String(dependentEmployeeCostEuros)
                          : ''
                      }
                      onChange={() => {}} // Read-only, calculated field
                      placeholder="0"
                      readonly
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            {dependentEmployeeCostEuros !== undefined && dependentEmployeeCostEuros > 0 && (
              <Callout
                type="info"
                title={i18n.t('benefits.healthcare.plan_wizard.employee_cost_step.callout.title')}
                description={i18n.t(
                  'benefits.healthcare.plan_wizard.employee_cost_step.callout.description',
                  {
                    amount: MoneyWithCurrency({
                      amount: toCents(dependentEmployeeCostEuros),
                      currency,
                      locale,
                    }),
                  }
                )}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  )
}

// Define step as factory function (like working wizards) instead of direct object
export const dependentsStep = (
  locale: string,
  currency: string
): WizardStep<HealthcarePlanData> => ({
  title: i18n.t('benefits.healthcare.plan_wizard.dependents_step.title'),
  render: (props) => <DependentsStep {...props} locale={locale} currency={currency} />,
  nextText: i18n.t('common.continue'),
  isCompleted: (data: HealthcarePlanData) => {
    // Use Zod validation for completion check
    const validationResult = dependentsStepSchema(locale, currency).safeParse(data)
    return validationResult.success
  },
})
